import React, { memo, forwardRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Icon from '../Icon';

const TabButton = memo(
    forwardRef(
        (
            {
                tabKey,
                title,
                active,
                onClick,
                onMouseOver,
                onMouseOut,
                onClickForPopover,
                fixed,
                editable,
                showClose,
                onClose,
                popoverContent,
                popoverWidth,
                classes,
                override,
            },
            ref,
        ) => {
            const className = useMemo(
                () =>
                    classnames(classes.tab, {
                        [classes.tabActive]: active,
                    }),
                [active, classes],
            );

            const tabId = `tab-${tabKey}`;
            const panelId = `panel-${tabKey}`;

            const handleClick = useCallback(
                (e) => {
                    e.preventDefault();
                    onClick(tabKey);
                },
                [onClick, tabKey],
            );

            const handleCloseClick = useCallback(
                (e) => {
                    e.stopPropagation();
                    onClose(e, tabKey);
                },
                [onClose, tabKey],
            );

            const handleKeyDown = useCallback(
                (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick(tabKey);
                    }
                },
                [onClick, tabKey],
            );

            let finalTitle = null;
            if (popoverContent && !active) {
                finalTitle = (
                    <div
                        className={classes.tabWithPopover}
                        data-popover-trigger="true"
                        onMouseOver={(e) => onMouseOver(e, popoverContent, popoverWidth)}
                        onMouseOut={(e) => onMouseOut(e)}
                        onClick={onClickForPopover}
                    >
                        {title}
                        {!fixed && (
                            <Icon name="close" onClick={handleCloseClick} {...override.close} />
                        )}
                    </div>
                );
            } else if (editable && showClose) {
                finalTitle = (
                    <>
                        {title}
                        {!fixed && (
                            <Icon name="close" onClick={handleCloseClick} {...override.close} />
                        )}
                    </>
                );
            } else {
                finalTitle = title;
            }

            return (
                <div
                    ref={ref}
                    className={className}
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    role="tab"
                    id={tabId}
                    aria-selected={active}
                    aria-controls={panelId}
                    tabIndex={active ? 0 : -1}
                >
                    {finalTitle}
                </div>
            );
        },
    ),
);

TabButton.propTypes = {
    /** Unique key identifier for the tab */
    tabKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    /** Content to display in the tab header (text or component) */
    title: PropTypes.node,
    /** Whether this tab is currently active/selected */
    active: PropTypes.bool,
    /** Callback when the tab is clicked */
    onClick: PropTypes.func.isRequired,
    /** Callback when mouse enters the tab */
    onMouseOver: PropTypes.func,
    /** Callback when mouse leaves the tab */
    onMouseOut: PropTypes.func,
    /** Callback when clicking for popover interaction */
    onClickForPopover: PropTypes.func,
    /** When true, the tab cannot be closed */
    fixed: PropTypes.bool,
    /** When true, enables editing mode with close button */
    editable: PropTypes.bool,
    /** When true, shows the close button */
    showClose: PropTypes.bool,
    /** Callback when the close button is clicked */
    onClose: PropTypes.func,
    /** Content to show in a popover when hovering */
    popoverContent: PropTypes.node,
    /** Width of the popover in pixels */
    popoverWidth: PropTypes.number,
    /** Classes object provided by the styling system */
    classes: PropTypes.object,
    /** Override object for styling inner elements */
    override: PropTypes.object,
};

export default TabButton;
